import { requireDashboardOrigin } from "@api/features/dashboard-auth/origin/dashboardOriginGuard"
import { serverRouteConfig } from "@server/server-route-config"
import { dashboardApiErrorMessage } from "@site-server/dashboard-api-errors"
import { createFileRoute } from "@tanstack/react-router"
import * as z from "zod"

const DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE =
  "Use at least 8 characters with uppercase, lowercase, and a number."

const passwordResetConfirmInputSchema = z.object({
  token: z.string().min(1).max(4096),
  password: z
    .string()
    .min(8, DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE)
    .max(128, "Password must be 128 characters or fewer.")
    .regex(/[a-z]/, DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE)
    .regex(/[A-Z]/, DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE)
    .regex(/[0-9]/, DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE),
})

export const Route = createFileRoute("/api/dashboard/auth/password-reset/confirm")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          try {
            const input = passwordResetConfirmInputSchema.parse(await request.json())
            const { confirmDashboardPasswordReset } = await import(
              "@api/features/dashboard-auth/password-reset/dashboardPasswordResetApi"
            )
            await confirmDashboardPasswordReset(input)
            return Response.json({ reset: true })
          } catch (error) {
            return Response.json(
              {
                error: dashboardPasswordResetConfirmErrorMessage(error),
              },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)

export function dashboardPasswordResetConfirmErrorMessage(error: unknown) {
  if (error instanceof z.ZodError) {
    if (error.issues.some((issue) => issue.path[0] === "password")) {
      return DASHBOARD_PASSWORD_REQUIREMENT_MESSAGE
    }
    return "Password reset link is invalid."
  }

  if (error instanceof SyntaxError) return "Could not reset password."
  return dashboardApiErrorMessage(error, "Could not reset password.")
}
